import { Box, Button, CircularProgress } from '@mui/material'
import { ReactElement, Suspense, lazy, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { AddVideoIcon } from 'src/v3/core/icons'
import { usePageTour } from 'src/v3/tours/hooks/use-page-tour'
import {
  fetchVideoLibrary,
  fetchLinkedServices,
  selectVideoLibrary,
  selectVideoLibraryStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectedLinkedServiceChanged,
} from '../create.slice'
import { deleteVideo, migrateVideo } from '../utils/api'
import VideoCard from './components/video-card'
import { useLibraryFilters } from '../shared/hooks/use-library-filters'
import LibraryPageLayout from '../shared/components/library-page-layout'

let VideoModal = lazy(() => import('../course/components/modals/video-modal'))
let CreateVideoModal = lazy(() => import('./components/create-video-modal'))

let ModalFallback = (
  <Box className="fixed inset-0 z-50 flex items-center justify-center">
    <CircularProgress />
  </Box>
)

export default function VideoLibraryPage(): ReactElement {
  useScrollToTop()
  usePageTour('video-library')
  let dispatch = useAppDispatch()
  let videos = useAppSelector(selectVideoLibrary)
  let videoLibraryStatus = useAppSelector(selectVideoLibraryStatus)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  useAutoFetch(videoLibraryStatus, fetchVideoLibrary)
  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  let {
    linkedServiceFilter,
    courseFilter,
    search,
    setLinkedServiceFilter,
    setCourseFilter,
    setSearch,
    resetFilters,
    isFiltered,
    filteredItems: filteredVideos,
    distinctCourses,
  } = useLibraryFilters(videos)

  let [createVideoOpen, setCreateVideoOpen] = useState(false)
  let [editVideoID, setEditVideoID] = useState<string | null>(null)
  let [deleteVideoID, setDeleteVideoID] = useState<string | null>(null)
  let [isDeleting, setIsDeleting] = useState(false)

  let { showSuccess, showError, NotificationComponent } = useNotifications()

  let videoToEdit = useMemo(
    () => videos.find((v) => v.id === editVideoID),
    [videos, editVideoID],
  )

  let handleMigrate = (videoID: string) => {
    migrateVideo({ contentID: videoID })
      .then(() => {
        dispatch(fetchVideoLibrary())
        showSuccess('Video migrated successfully')
      })
      .catch(() => showError('Failed to migrate video'))
  }

  let handleDeleteConfirm = () => {
    if (!deleteVideoID) return
    setIsDeleting(true)
    deleteVideo(deleteVideoID)
      .then(() => {
        dispatch(fetchVideoLibrary())
        showSuccess('Video deleted successfully')
        setDeleteVideoID(null)
      })
      .catch(() => showError('Failed to delete video'))
      .finally(() => setIsDeleting(false))
  }

  return (
    <>
      <LibraryPageLayout
        pageTitle="Video Library"
        createButton={
          <Button
            variant="contained"
            size="large"
            startIcon={<AddVideoIcon />}
            onClick={() => setCreateVideoOpen(true)}
          >
            Create Video
          </Button>
        }
        itemCountLabel={`${filteredVideos.length} videos`}
        linkedServices={linkedServices}
        filters={{
          linkedServiceFilter,
          courseFilter,
          search,
          onLinkedServiceFilterChange: (v) => {
            setLinkedServiceFilter(v)
            setCourseFilter('all')
            dispatch(selectedLinkedServiceChanged(v))
          },
          onCourseFilterChange: setCourseFilter,
          onSearchChange: setSearch,
          onResetFilters: resetFilters,
          isFiltered,
          distinctCourses,
          filteredItemCount: filteredVideos.length,
          totalItemCount: videos.length,
        }}
        isLoading={videoLibraryStatus === 'pending'}
        emptyMessage="No videos found."
        noResultsMessage="No videos match your filters."
        loadingAriaLabel="Loading videos"
        deleteDialog={{
          open: !!deleteVideoID,
          onClose: () => setDeleteVideoID(null),
          onConfirm: handleDeleteConfirm,
          title: 'Delete Video',
          itemName: videos.find((v) => v.id === deleteVideoID)?.name,
          isDeleting,
        }}
      >
        {filteredVideos.map((video) => (
          <VideoCard
            key={video.id}
            video={video}
            onEdit={() => setEditVideoID(video.id)}
            onDelete={() => setDeleteVideoID(video.id)}
            onMigrate={() => handleMigrate(video.id)}
          />
        ))}
      </LibraryPageLayout>

      {createVideoOpen && (
        <Suspense fallback={ModalFallback}>
          <CreateVideoModal
            isOpen
            onClose={() => setCreateVideoOpen(false)}
            linkedServices={linkedServices}
            defaultLinkedServiceID={
              linkedServiceFilter !== 'all' ? linkedServiceFilter : ''
            }
            onSuccess={() => {
              dispatch(fetchVideoLibrary())
              showSuccess('Video created successfully')
              setCreateVideoOpen(false)
            }}
            onFailure={() => showError('Failed to create video')}
          />
        </Suspense>
      )}

      {editVideoID && videoToEdit && (
        <Suspense fallback={ModalFallback}>
          <VideoModal
            isOpen
            onClose={() => setEditVideoID(null)}
            courseID=""
            videos={[]}
            onSuccess={() => {
              dispatch(fetchVideoLibrary())
              showSuccess('Video updated successfully')
              setEditVideoID(null)
            }}
            onFailure={() => showError('Failed to update video')}
            videoToEdit={videoToEdit}
          />
        </Suspense>
      )}

      {NotificationComponent}
    </>
  )
}
